import { useState } from 'react';
import { Select, useToast } from '@dreamtree-org/twreact-ui';
import { useCan } from '@rbac/useCan';
import { errorText } from '@/lib/format';
import { useCrudList, useCrudWrite } from '../hooks';
import { brandOptions, findBrandByName } from '../lib/brands';

/**
 * Brand picker (D39): searchable over the store's brand list, brands for `typeKey`
 * first. With catalog:create a missing brand can be added on the spot ("creatable").
 * `onChange(id, name)` gets the brand id as a string and its name.
 */
export default function BrandSelect({ value, onChange, typeKey, label = 'Brand', required, error, helperText, placeholder = 'Search brands…', excludeId }) {
  const can = useCan();
  const { toast } = useToast();
  const list = useCrudList('Brand', { orderBy: { column: 'name', direction: 'asc' }, limit: 1000 });
  const write = useCrudWrite();
  const [adding, setAdding] = useState(false);
  const brands = (list.data || []).filter((b) => excludeId == null || String(b.id) !== String(excludeId));
  const pick = (id) => {
    const b = brands.find((x) => String(x.id) === String(id));
    onChange(id ? String(id) : '', b ? b.name : '');
  };

  const create = async (name) => {
    const existing = findBrandByName(brands, name);
    if (existing) return pick(existing.id);
    setAdding(true);
    try {
      const row = await write.create('Brand', { name, product_types: typeKey ? [typeKey] : null });
      await list.reload();
      onChange(String(row.id), row.name);
      toast.success('Brand added', row.name);
    } catch (ex) {
      // Someone else added it meanwhile: reload and pick it.
      if (ex.response && ex.response.data && ex.response.data.code === 'BRAND_EXISTS') {
        const res = await write.run({ model: 'Brand', action: 'list', limit: 1000 });
        const b = findBrandByName(Array.isArray(res) ? res : (res && res.data) || [], name);
        await list.reload();
        if (b) onChange(String(b.id), b.name);
      } else {
        toast.error('Could not add the brand', errorText(ex));
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <Select
      label={label}
      required={required}
      placeholder={placeholder}
      options={brandOptions(brands, typeKey, value)}
      value={value ? String(value) : ''}
      onChange={pick}
      searchable
      creatable={can('catalog', 'create')}
      onCreateOption={create}
      loading={list.loading || adding}
      error={error || list.error || undefined}
      helperText={helperText || (can('catalog', 'create') ? 'Not listed? Type the name to add it.' : undefined)}
      closeOnSelect
    />
  );
}
